import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import onboardingService, { BrandKitFile } from "../services/onboardingService";

type View = "choice" | "website" | "upload";

const Brand: React.FC = () => {
  const location = useLocation();
  const isOnboarding = location.pathname.includes("onboarding");
  const [view, setView] = useState<View>("choice");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [savedWebsiteUrl, setSavedWebsiteUrl] = useState<string | null>(null);
  const [brandKitFiles, setBrandKitFiles] = useState<BrandKitFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlSaved, setUrlSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    onboardingService.getLaunchChecklist().then((data) => {
      setSavedWebsiteUrl(data.brand_website_url);
      setWebsiteUrl(data.brand_website_url || "");
      setBrandKitFiles(data.brand_kit_files || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSaveUrl = async () => {
    if (!websiteUrl.trim()) return;
    setSavingUrl(true);
    setError(null);
    try {
      await onboardingService.saveBrandWebsiteUrl(websiteUrl.trim());
      setSavedWebsiteUrl(websiteUrl.trim());
      setUrlSaved(true);
      setTimeout(() => setUrlSaved(false), 1500);
    } catch {
      setError("Failed to save URL. Please try again.");
    } finally {
      setSavingUrl(false);
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const updated = await onboardingService.uploadBrandFiles(Array.from(files));
      setBrandKitFiles(updated);
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (url: string) => {
    try {
      const updated = await onboardingService.deleteBrandFile(url);
      setBrandKitFiles(updated);
    } catch {
      setError("Failed to remove file. Please try again.");
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  if (loading) {
    return <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>Loading...</div>;
  }

  return (
    <div style={!isOnboarding ? { maxWidth: "800px", paddingTop: "16px" } : {}}>
      {!isOnboarding && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 style={{ display: "inline-block" }}>🎨 Brand</h1>
        </div>
      )}
      {view !== "choice" && (
        <button
          onClick={() => setView("choice")}
          style={{
            background: "none",
            border: "none",
            color: "#6b7280",
            cursor: "pointer",
            fontSize: "14px",
            padding: "0 0 20px 0",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          ← Back
        </button>
      )}

      {view === "choice" && (
        <div>
          <p style={{ color: "#6b7280", marginTop: 0, marginBottom: "28px" }}>
            Share your brand with us so we can style your newsletter to match.
          </p>

          {(savedWebsiteUrl || brandKitFiles.length > 0) && (
            <div
              style={{
                marginBottom: "28px",
                padding: "16px 20px",
                backgroundColor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "10px",
                fontSize: "14px",
                color: "#166534",
              }}
            >
              {savedWebsiteUrl && (
                <div style={{ marginBottom: brandKitFiles.length > 0 ? "8px" : 0 }}>
                  🌐 <strong>Website:</strong> {savedWebsiteUrl}
                </div>
              )}
              {brandKitFiles.length > 0 && (
                <div>
                  📁 <strong>{brandKitFiles.length} file{brandKitFiles.length !== 1 ? "s" : ""} uploaded</strong>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "16px" }}>
            <button
              onClick={() => setView("website")}
              style={{
                flex: 1,
                padding: "32px 24px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                background: "#fff",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>🌐</div>
              <div style={{ fontWeight: 600, fontSize: "16px", marginBottom: "8px" }}>
                Use your website
              </div>
              <div style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.4" }}>
                We'll model your newsletter design off your existing website or landing page.
              </div>
            </button>

            <button
              onClick={() => setView("upload")}
              style={{
                flex: 1,
                padding: "32px 24px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                background: "#fff",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              <div style={{ fontSize: "28px", marginBottom: "12px" }}>📁</div>
              <div style={{ fontWeight: 600, fontSize: "16px", marginBottom: "8px" }}>
                Upload your brand kit
              </div>
              <div style={{ color: "#6b7280", fontSize: "14px", lineHeight: "1.4" }}>
                Share logos, brand guidelines, or any files that represent your brand.
              </div>
            </button>
          </div>
        </div>
      )}

      {view === "website" && (
        <div>
          <p style={{ color: "#6b7280", marginTop: 0, marginBottom: "24px" }}>
            Enter the URL of your website or landing page. We'll use it to match your newsletter's look and feel.
          </p>

          <div style={{ maxWidth: "450px" }}>
            <input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveUrl(); }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "15px",
                boxSizing: "border-box",
                marginBottom: "16px",
              }}
            />

            {error && <div style={{ color: "#991b1b", fontSize: "14px", marginBottom: "12px" }}>{error}</div>}

            <button
              className="primary-button"
              onClick={handleSaveUrl}
              disabled={!websiteUrl.trim() || savingUrl}
            >
              {savingUrl ? "Saving..." : "Save"}
            </button>

            {urlSaved && (
              <div style={{
                marginTop: "16px",
                backgroundColor: "#e0f7df",
                color: "#2e7d32",
                border: "1px solid #2e7d32",
                borderRadius: "8px",
                padding: "8px 16px",
              }}>
                ✅ <span style={{ marginLeft: "8px" }}>URL saved!</span>
              </div>
            )}
          </div>
        </div>
      )}

      {view === "upload" && (
        <div>
          <p style={{ color: "#6b7280", marginTop: 0, marginBottom: "24px" }}>
            Upload logos, brand guidelines, PDFs, or zip files, anything that captures your brand.
          </p>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? "#6366f1" : "#d1d5db"}`,
              borderRadius: "10px",
              padding: "40px 24px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: dragging ? "#eef2ff" : "#fafafa",
              transition: "all 0.15s ease",
              marginBottom: "20px",
            }}
          >
            <div style={{ fontSize: "28px", marginBottom: "8px" }}>📎</div>
            <div style={{ fontWeight: 500, marginBottom: "4px" }}>
              {uploading ? "Uploading..." : "Drop files here or click to browse"}
            </div>
            <div style={{ color: "#9ca3af", fontSize: "13px" }}>
              PNG, JPG, SVG, PDF, ZIP accepted
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".png,.jpg,.jpeg,.svg,.pdf,.zip,.ai,.eps"
              style={{ display: "none" }}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>

          {error && <div style={{ color: "#991b1b", fontSize: "14px", marginBottom: "12px" }}>{error}</div>}

          {brandKitFiles.length > 0 && (
            <div>
              <div style={{ fontWeight: 500, marginBottom: "10px", fontSize: "14px" }}>
                Uploaded files
              </div>
              {brandKitFiles.map((file) => (
                <div
                  key={file.url}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    marginBottom: "8px",
                    fontSize: "14px",
                  }}
                >
                  <span>📄 {file.filename}</span>
                  <button
                    onClick={() => handleDeleteFile(file.url)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#9ca3af",
                      cursor: "pointer",
                      fontSize: "16px",
                      lineHeight: 1,
                    }}
                    title="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Brand;
